import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Database, FileText, Shield, AlertTriangle,
    CheckCircle, Clock, Upload, Table as TableIcon,
    Trash2, Eye, FileDigit, DollarSign, Save, X,
    Download
} from 'lucide-react';
import { getRecordById, analyzeFraud, API_BASE_URL } from '../api/client';
import { getFieldLabel } from '../constants';
import ManualInvoiceList from '../components/ManualInvoices/ManualInvoiceList';

export default function RecordDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    // États Dossier
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // États Factures
    const [invoices, setInvoices] = useState([]);
    const [uploadingInvoice, setUploadingInvoice] = useState(false);
    const [expandedInvoice, setExpandedInvoice] = useState(null);
    const [editingCell, setEditingCell] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [exportingTables, setExportingTables] = useState({});

    // États Fraude
    const [fraudAnalysis, setFraudAnalysis] = useState(null);
    const [fraudLoading, setFraudLoading] = useState(false);
    const [fraudModelProvider, setFraudModelProvider] = useState("gemini");

    // État pour les onglets
    const [activeTab, setActiveTab] = useState('data');

    useEffect(() => {
        fetchInitialData();
    }, [id]);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const recordResponse = await getRecordById(id);
            setRecord(recordResponse.record);

            const response = await fetch(`${API_BASE_URL}/dossier/${id}/factures`);
            if (response.ok) {
                const data = await response.json();
                setInvoices(data.factures || []);
            }
        } catch (err) {
            console.error(err);
            setError("Erreur lors du chargement des données.");
        } finally {
            setLoading(false);
        }
    };

    // --- FONCTION D'EXPORT EXCEL POUR UNE TABLE SPÉCIFIQUE ---
    const exportSingleTableToExcel = async (invoice, tableIndex) => {
        const key = `${invoice.id}_${tableIndex}`;
        setExportingTables(prev => ({ ...prev, [key]: true }));

        try {
            const response = await fetch(
                `${API_BASE_URL}/facture/${invoice.id}/export-table/${tableIndex}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (!response.ok) {
                throw new Error("Erreur lors de la génération du fichier Excel");
            }

            // Télécharger le fichier
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            // Nom du fichier basé sur le tableau
            const tableName = invoice.extracted_data.tables[tableIndex].title || `Tableau_${tableIndex + 1}`;
            a.download = `${tableName}_${invoice.original_filename.replace(/\.[^/.]+$/, "")}.xlsx`;

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);

            alert("Export Excel réussi !");

        } catch (error) {
            console.error("Erreur d'export:", error);
            alert("Erreur lors de l'export vers Excel: " + error.message);
        } finally {
            setExportingTables(prev => {
                const newState = { ...prev };
                delete newState[key];
                return newState;
            });
        }
    };

    // --- GESTION DES FACTURES ---
    const handleInvoiceUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setUploadingInvoice(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('model_provider', 'gemini');

        try {
            const res = await fetch(`${API_BASE_URL}/dossier/${id}/upload-facture`, {
                method: 'POST',
                body: formData
            });

            if (!res.ok) throw new Error("Erreur upload");

            alert("Facture analysée et ajoutée !");
            fetchInitialData();
        } catch (err) {
            alert("Erreur lors de l'envoi de la facture: " + err.message);
        } finally {
            setUploadingInvoice(false);
        }
    };

    const handleDeleteInvoice = async (invoiceId) => {
        if (!window.confirm("Supprimer cette facture ?")) return;
        try {
            await fetch(`${API_BASE_URL}/facture/${invoiceId}`, { method: 'DELETE' });
            setInvoices(invoices.filter(inv => inv.id !== invoiceId));
            alert("Facture supprimée avec succès !");
        } catch (err) {
            alert("Erreur suppression");
        }
    };

    // --- GESTION DE L'ÉDITION DES CELLULES ---
    const handleCellEdit = (invoiceId, tableIdx, rowIdx, cellIdx, newValue) => {
        setInvoices(prevInvoices =>
            prevInvoices.map(inv => {
                if (inv.id !== invoiceId) return inv;

                const updatedTables = [...(inv.extracted_data?.tables || [])];
                if (updatedTables[tableIdx]?.rows?.[rowIdx]) {
                    updatedTables[tableIdx].rows[rowIdx][cellIdx] = newValue;
                }

                return {
                    ...inv,
                    extracted_data: {
                        ...inv.extracted_data,
                        tables: updatedTables
                    }
                };
            })
        );
        setHasUnsavedChanges(true);
    };

    const handleSaveInvoiceChanges = async (invoiceId) => {
        try {
            const invoice = invoices.find(inv => inv.id === invoiceId);
            if (!invoice) return;

            const response = await fetch(`${API_BASE_URL}/facture/${invoiceId}/update`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    extracted_data: invoice.extracted_data
                })
            });

            if (!response.ok) throw new Error("Erreur de sauvegarde");

            alert("Modifications sauvegardées avec succès !");
            setEditingCell(null);
            setHasUnsavedChanges(false);
        } catch (err) {
            alert("Erreur lors de la sauvegarde: " + err.message);
        }
    };

    const handleCancelEdit = (invoiceId) => {
        setEditingCell(null);
        setHasUnsavedChanges(false);
        fetchInitialData();
    };

    // --- GESTION FRAUDE ---
    const handleAnalyzeFraud = async () => {
        setFraudLoading(true);
        try {
            const result = await analyzeFraud(id, fraudModelProvider);
            setFraudAnalysis(result.analysis);
        } catch (err) {
            alert("Erreur analyse fraude");
        } finally {
            setFraudLoading(false);
        }
    };

    // --- CALCUL DES TOTAUX ---
    const calculateTableTotals = (table) => {
        if (!table.rows || table.rows.length === 0) return null;

        const totals = {};

        // Fonction helper pour parser les nombres au format européen
        const parseEuropeanNumber = (value) => {
            if (!value) return 0;
            // Supprimer les espaces et points (séparateurs de milliers)
            // Remplacer la virgule (séparateur décimal) par un point
            return parseFloat(
                String(value)
                    .replace(/\s/g, '')  // Supprimer espaces
                    .replace(/\./g, '')  // Supprimer points (milliers)
                    .replace(/,/g, '.')  // Remplacer virgule par point (décimal)
            ) || 0;
        };

        // Fonction helper pour formater en notation européenne
        const formatEuropeanNumber = (number) => {
            // Formater avec 3 décimales
            const formatted = number.toFixed(3);
            // Séparer partie entière et décimale
            const [integerPart, decimalPart] = formatted.split('.');
            // Ajouter des espaces comme séparateurs de milliers
            const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            // Retourner avec virgule comme séparateur décimal
            return `${formattedInteger},${decimalPart}`;
        };

        table.columns.forEach((colName, colIdx) => {
            const lowerCol = colName.toLowerCase();

            if (lowerCol === 'montant') {
                const sum = table.rows.reduce((sum, row) => {
                    const val = parseEuropeanNumber(row[colIdx]);
                    return sum + val;
                }, 0);
                totals[colIdx] = formatEuropeanNumber(sum);
            }
            else if (lowerCol.includes('%') || lowerCol === 'tva' || lowerCol === 'r' || lowerCol === 'v') {
                const montantIdx = table.columns.findIndex(c => c.toLowerCase() === 'montant');
                if (montantIdx !== -1) {
                    const sum = table.rows.reduce((sum, row) => {
                        const montant = parseEuropeanNumber(row[montantIdx]);
                        const percentage = parseEuropeanNumber(row[colIdx]);
                        return sum + (montant * percentage / 100);
                    }, 0);
                    totals[colIdx] = formatEuropeanNumber(sum);
                }
            }
            else {
                totals[colIdx] = '';
            }
        });

        return totals;
    };

    // --- RENDU DES TABLEAUX (EXTRAIT DES FACTURES) ---
    const renderInvoiceTables = (invoice) => {
        if (!invoice.extracted_data || !invoice.extracted_data.tables) return null;

        const isEditingThisInvoice = editingCell?.invoiceId === invoice.id;

        return (
            <>
                {invoice.extracted_data.tables.map((table, idx) => {
                    const exportKey = `${invoice.id}_${idx}`;
                    const isExporting = exportingTables[exportKey];

                    return (
                        <div key={idx} className="mt-4 border border-gray-200 rounded-lg overflow-hidden text-sm">
                            <div className="bg-gray-50 px-3 py-2 font-semibold border-b border-gray-200 flex justify-between items-center">
                                <span>Tableau détecté {idx + 1}</span>
                                <div className="flex items-center gap-3">
                                    {!isEditingThisInvoice && (
                                        <span className="text-xs text-gray-500 font-normal">
                                            Cliquez sur une cellule pour modifier
                                        </span>
                                    )}
                                    <button
                                        onClick={() => exportSingleTableToExcel(invoice, idx)}
                                        disabled={isExporting}
                                        className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Exporter ce tableau vers Excel"
                                    >
                                        {isExporting ? (
                                            <>
                                                <Clock className="w-3.5 h-3.5 animate-spin" />
                                                Export...
                                            </>
                                        ) : (
                                            <>
                                                <Download className="w-3.5 h-3.5" />
                                                Excel
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {table.columns?.map((col, cIdx) => (
                                                <th key={cIdx} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                                    {col}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {table.rows?.map((row, rIdx) => (
                                            <tr key={rIdx} className="hover:bg-gray-50 transition-colors">
                                                {row.map((cell, cellIdx) => {
                                                    const isThisCellEditing =
                                                        editingCell?.invoiceId === invoice.id &&
                                                        editingCell?.tableIdx === idx &&
                                                        editingCell?.rowIdx === rIdx &&
                                                        editingCell?.cellIdx === cellIdx;

                                                    return (
                                                        <td
                                                            key={cellIdx}
                                                            className="px-3 py-2 text-gray-700"
                                                            onClick={() => !isThisCellEditing && setEditingCell({
                                                                invoiceId: invoice.id,
                                                                tableIdx: idx,
                                                                rowIdx: rIdx,
                                                                cellIdx: cellIdx
                                                            })}
                                                        >
                                                            {isThisCellEditing ? (
                                                                <input
                                                                    type="text"
                                                                    value={cell}
                                                                    onChange={(e) => handleCellEdit(
                                                                        invoice.id,
                                                                        idx,
                                                                        rIdx,
                                                                        cellIdx,
                                                                        e.target.value
                                                                    )}
                                                                    onBlur={() => setEditingCell(null)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') setEditingCell(null);
                                                                        if (e.key === 'Escape') {
                                                                            setEditingCell(null);
                                                                            handleCancelEdit(invoice.id);
                                                                        }
                                                                    }}
                                                                    className="w-full px-2 py-1 border-2 border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50"
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <span className="whitespace-nowrap cursor-pointer hover:bg-blue-50 px-2 py-1 rounded transition-colors">
                                                                    {cell ? String(cell) : <span className="text-gray-400 italic">Vide</span>}
                                                                </span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}

                                        {/* LIGNE DE TOTAUX */}
                                        {(() => {
                                            const totals = calculateTableTotals(table);
                                            if (!totals) return null;

                                            return (
                                                <tr className="bg-blue-50 border-t-2 border-blue-200 font-bold">
                                                    {table.columns.map((col, cellIdx) => (
                                                        <td key={cellIdx} className="px-3 py-3 text-gray-900">
                                                            {cellIdx === 0 ? (
                                                                <span className="text-blue-700">TOTAL</span>
                                                            ) : (
                                                                totals[cellIdx] && (
                                                                    <span className="text-blue-700">
                                                                        {totals[cellIdx]}
                                                                    </span>
                                                                )
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })}

                {hasUnsavedChanges && isEditingThisInvoice && (
                    <div className="mt-4 flex justify-end gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex-1 flex items-center gap-2 text-amber-700 text-sm">
                            <AlertTriangle className="w-4 h-4" />
                            <span>Vous avez des modifications non sauvegardées</span>
                        </div>
                        <button
                            onClick={() => handleCancelEdit(invoice.id)}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Annuler
                        </button>
                        <button
                            onClick={() => handleSaveInvoiceChanges(invoice.id)}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" />
                            Sauvegarder les modifications
                        </button>
                    </div>
                )}
            </>
        );
    };

    const renderFieldValue = (value) => {
        if (!value) return <span className="text-gray-300 italic">Non renseigné</span>;
        return <span className="text-gray-800 font-medium">{String(value)}</span>;
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
            <Clock className="animate-spin text-blue-600 mr-2" /> Chargement du dossier...
        </div>
    );

    if (!record) return <div className="p-8 text-center text-red-500">Dossier introuvable</div>;

    return (
        <div className="min-h-screen bg-gray-50 pb-12">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-20 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/records')} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                <FileText className="w-5 h-5 text-blue-600" />
                                Dossier {record.numero_dossier || "Sans numéro"}
                            </h1>
                            <p className="text-xs text-gray-500">ID: {id}</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium border border-blue-200">
                            {record.marque || "Véhicule inconnu"}
                        </span>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* COLONNE GAUCHE (2/3) : Onglets */}
                    <div className="lg:col-span-2">
                        {/* Barre d'onglets */}
                        <div className="flex border-b border-gray-200 bg-white rounded-t-xl overflow-hidden">
                            <button
                                onClick={() => setActiveTab('data')}
                                className={`flex-1 py-4 px-6 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'data'
                                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                            >
                                <Database className="w-4 h-4" />
                                Données Extraites
                            </button>
                            <button
                                onClick={() => setActiveTab('invoices')}
                                className={`flex-1 py-4 px-6 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'invoices'
                                    ? 'bg-indigo-50 text-indigo-700 border-b-2 border-indigo-500'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                            >
                                <DollarSign className="w-4 h-4" />
                                Factures & Devis
                                {invoices.length > 0 && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded-full">
                                        {invoices.length}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab('manual_invoices')}
                                className={`flex-1 py-4 px-6 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'manual_invoices'
                                    ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-500'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                            >
                                <FileText className="w-4 h-4" />
                                Factures Manuelles
                            </button>
                        </div>

                        {/* Contenu des onglets */}
                        <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 min-h-[500px]">
                            {/* Onglet Données Extraites */}
                            {activeTab === 'data' && (
                                <div className="p-6">
                                    <div className="mb-6 flex justify-between items-center">
                                        <h2 className="font-bold text-gray-800 flex items-center gap-2">
                                            <Database className="w-4 h-4 text-gray-500" /> Données Extraites
                                        </h2>
                                        <span className="text-xs text-gray-400">Fichier: {record.original_filename}</span>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                        {Object.entries(record).map(([key, value]) => {
                                            if (['id', 'transcription', 'created_at', 'original_filename', 'extraction_confidence'].includes(key)) return null;
                                            return (
                                                <div key={key} className="border-b border-gray-100 pb-1">
                                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">
                                                        {getFieldLabel(key) || key}
                                                    </label>
                                                    <div className="text-sm">{renderFieldValue(value)}</div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Onglet Factures & Devis */}
                            {activeTab === 'invoices' && (
                                <div className="p-6">
                                    <div className="mb-6 flex justify-between items-center">
                                        <h2 className="font-bold text-indigo-900 flex items-center gap-2">
                                            <DollarSign className="w-4 h-4" /> Factures & Devis
                                        </h2>
                                        <div className="flex gap-2">
                                            <label className={`flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-indigo-700 transition ${uploadingInvoice ? 'opacity-50 pointer-events-none' : ''}`}>
                                                {uploadingInvoice ? <Clock className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                                                {uploadingInvoice ? "Analyse..." : "Ajouter une facture"}
                                                <input type="file" className="hidden" onChange={handleInvoiceUpload} accept=".pdf,.jpg,.png" />
                                            </label>
                                        </div>
                                    </div>

                                    {invoices.length === 0 ? (
                                        <div className="text-center py-16 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                                            <TableIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                            <p className="text-gray-500 text-sm mb-2">Aucune facture associée à ce dossier.</p>
                                            <p className="text-gray-400 text-xs">Ajoutez une facture pour commencer l'analyse</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {invoices.map((inv) => (
                                                <div key={inv.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                                                <FileDigit className="w-5 h-5" />
                                                            </div>
                                                            <div>
                                                                <p className="font-medium text-gray-900">{inv.original_filename}</p>
                                                                <p className="text-xs text-gray-500">Ajouté le {new Date(inv.created_at).toLocaleDateString()}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => setExpandedInvoice(expandedInvoice === inv.id ? null : inv.id)}
                                                                className="text-gray-400 hover:text-blue-600 p-1"
                                                                title="Voir les données extraites"
                                                            >
                                                                <Eye className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteInvoice(inv.id)}
                                                                className="text-gray-400 hover:text-red-600 p-1"
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 className="w-5 h-5" />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Zone dépliable pour voir les tableaux extraits */}
                                                    {expandedInvoice === inv.id && (
                                                        <div className="mt-4 pt-4 border-t border-gray-100 animation-fade-in">
                                                            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Contenu détecté par IA :</p>
                                                            {renderInvoiceTables(inv)}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Onglet Factures Manuelles */}
                            {activeTab === 'manual_invoices' && (
                                <div className="p-6">
                                    <ManualInvoiceList recordId={id} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* COLONNE DROITE (1/3) : Actions & Fraude */}
                    <div className="space-y-6">

                        {/* Carte Analyse Fraude */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-24">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <Shield className="w-5 h-5 text-red-500" />
                                Analyse Anti-Fraude
                            </h3>

                            <div className="mb-4">
                                <label className="text-xs text-gray-500 mb-1 block">Modèle d'analyse</label>
                                <select
                                    value={fraudModelProvider}
                                    onChange={(e) => setFraudModelProvider(e.target.value)}
                                    className="w-full text-sm border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                >
                                    <option value="gemini">Gemini 2.0 Flash (Google)</option>
                                    <option value="qwen">Qwen 2.5 (OpenRouter)</option>
                                </select>
                            </div>

                            <button
                                onClick={handleAnalyzeFraud}
                                disabled={fraudLoading}
                                className="w-full py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg font-semibold hover:from-red-700 hover:to-red-800 transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-70"
                            >
                                {fraudLoading ? <Clock className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                                {fraudLoading ? "Analyse en cours..." : "Lancer l'analyse"}
                            </button>

                            {fraudAnalysis && (
                                <div className="mt-6 animate-fade-in">
                                    <div className={`p-4 rounded-lg mb-4 ${fraudAnalysis.fraud_risk_level === 'élevé' ? 'bg-red-50 border border-red-200' :
                                        fraudAnalysis.fraud_risk_level === 'moyen' ? 'bg-orange-50 border border-orange-200' :
                                            'bg-green-50 border border-green-200'
                                        }`}>
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-sm font-bold text-gray-700">Risque estimé</span>
                                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${fraudAnalysis.fraud_risk_level === 'élevé' ? 'bg-red-200 text-red-800' :
                                                fraudAnalysis.fraud_risk_level === 'moyen' ? 'bg-orange-200 text-orange-800' :
                                                    'bg-green-200 text-green-800'
                                                }`}>
                                                {fraudAnalysis.fraud_risk_level}
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className={`h-2 rounded-full ${fraudAnalysis.fraud_score > 70 ? 'bg-red-500' :
                                                    fraudAnalysis.fraud_score > 40 ? 'bg-orange-500' : 'bg-green-500'
                                                    }`}
                                                style={{ width: `${fraudAnalysis.fraud_score}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    {fraudAnalysis.indicators && fraudAnalysis.indicators.length > 0 && (
                                        <div className="space-y-3">
                                            <h4 className="text-xs font-bold text-gray-500 uppercase">Anomalies détectées</h4>
                                            {fraudAnalysis.indicators.map((ind, i) => (
                                                <div key={i} className="flex gap-2 items-start text-sm p-2 bg-gray-50 rounded">
                                                    <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <span className="font-semibold block">{ind.type}</span>
                                                        <span className="text-gray-600 text-xs">{ind.description}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {fraudAnalysis.summary && (
                                        <div className="mt-4 text-xs text-gray-500 italic border-t pt-2">
                                            "{fraudAnalysis.summary}"
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Carte info supp */}
                        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                            <h4 className="font-bold text-blue-800 flex items-center gap-2 mb-2">
                                <CheckCircle className="w-4 h-4" />
                                Statut du dossier
                            </h4>
                            <p className="text-sm text-blue-700">
                                Ce dossier est complet. Toutes les métadonnées ont été extraites avec succès.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}